import React, { useState, useEffect } from "react";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import Loading from "../../../Components/Loading";
import { formatKeys } from "../../../../utils/formHelper";
import { timeSheetSingleRowFields, getPayrollFields, getTimeSheetFields } from "../projectData";
import { errorToast } from "../../../../utils/toastHelper";
import PropTypes from "prop-types";
import DialogModal from "../../../Components/DialogModel";
import {
  GET_PROJECT_SUBMITTIMESHEETEXCEL,
  GET_PROJECT_SUBMITWEEKLYTIMESHEETEXCEL,
  GET_PROJECTPAYROLL_SUBMITTIMESHEETEXCEL,
  GET_WORK_AREAS
} from "../../../../utils/services/apiPath";
import { getApi, postApi } from "../../../../utils/services";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { detectFileVersion, getVersionLabel, versionsMatch } from "../../../../utils/fileVersionDetector";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";

const ProjectCertifiedPayrollSpreadsheet = ({
  id,
  isOpen,
  setIsOpen,
  timeSheetValue,
  selectedValue,
  apiMessage,
  setApiMessage,
  certifiedPayrollModal,
  setCertifiedPayrollModal,
  handlePayrollTimeSheet,
  projectInfo
}) => {
  const [uploadLoader, setUploadLoader] = useState(false);
  const [timeSheetModal, setTimeSheetModal] = useState(false);
  const [payrollSummaryModal, setPayrollSummaryModal] = useState(false);
  const [hasWorkAreas, setHasWorkAreas] = useState(false);
  const isAmendedAndSupplementFlag = useFeatureFlagEnabled("has-ammended-and-supplement-cpr-feature");

  // Version mismatch warning state
  const [versionWarning, setVersionWarning] = useState({
    show: false,
    fileVersion: "",
    projectVersion: ""
  });

  const TIMESHEET_UPLOAD_ERROR = "Timesheet Excel data failed to upload.";

  useEffect(() => {
    if (!id) {
      setHasWorkAreas(false);
      return;
    }

    let isMounted = true;

    const isVersionTwo = projectInfo?.cprVersionLabel?.includes("1/31/2028");
    if (!isVersionTwo) {
      setHasWorkAreas(false);
      return;
    }

    const checkWorkAreas = async () => {
      try {
        const response = await getApi(GET_WORK_AREAS(id));
        if (isMounted) {
          setHasWorkAreas(Array.isArray(response?.data) && response.data.length > 0);
        }
      } catch (error) {
        if (isMounted) {
          setHasWorkAreas(false);
        }
        console.error("Failed to fetch project work areas", { projectId: id, error });
      }
    };
    checkWorkAreas();

    return () => {
      isMounted = false;
    };
  }, [id, projectInfo?.cprVersionLabel]);

  const handleCancelTimeSheet = () => {
    setTimeSheetModal(false);
  };

  const handleCancelPayrollSummary = () => {
    setPayrollSummaryModal(false);
  };

  const handleCertifiedPayroll = () => {
    setCertifiedPayrollModal(false);
  };

  const handleCloseDialogpopup = () => {
    setIsOpen(false);
  };

  const handleUploadStepHook = async uploadedData => {
    // Detect file version from uploaded data
    const fileType = selectedValue === "1" ? "timesheet" : "payroll";
    const detectedVersion = detectFileVersion(uploadedData, fileType);

    const projectCprVersion = getVersionLabel(projectInfo?.cprVersionLabel);

    // Check if versions match
    const versionMatchResult = versionsMatch(detectedVersion.version, projectCprVersion);
    if (detectedVersion.version && !versionMatchResult.match) {
      // Show warning dialog and wait for user decision
      const userChoice = await new Promise(resolve => {
        setVersionWarning({
          show: true,
          fileVersion: detectedVersion.version,
          projectVersion: projectCprVersion,
          onCancel: () => {
            setVersionWarning({ show: false, fileVersion: "", projectVersion: "" });
            setIsOpen(false); // Close the import modal
            resolve(false); // User cancelled
          },
          onProceed: () => {
            setVersionWarning({ show: false, fileVersion: "", projectVersion: "" });
            resolve(true); // User chose to proceed
          }
        });
      });

      if (!userChoice) {
        // User clicked Cancel - halt upload by throwing an error
        throw new Error('Upload cancelled by user');
      }

      // User clicked Proceed - continue with upload
    }

    // Versions match or user chose to proceed, return data to continue
    return uploadedData;
  };

  const handleTimeSheetSubmit = async (data, selectedFile) => {
    const reader = new FileReader();
    setUploadLoader(true);
    reader.onload = async () => {
      const base64String = reader.result.split(",")[1];
      const mappedData = (data?.validData || []).map(row => {
        const rawPos = row["Labor Classification"] ?? row["Position"] ?? row.position ?? "";
        const position = typeof rawPos === "string" ? rawPos.trim() : String(rawPos || "");
        const mapped = { ...row, position };
        return mapped;
      });
      const payload = {
        projectId: id,
        fileModel: {
          base64String: base64String,
          fileName: selectedFile.name
        },
        excelData: formatKeys(mappedData)
      };

      try {
        const res = await postApi(`${GET_PROJECT_SUBMITWEEKLYTIMESHEETEXCEL}`, payload);
        if (res.statusCode === 200) {
          setUploadLoader(false);
          setTimeSheetModal(true);
          setApiMessage(res.message);
          handlePayrollTimeSheet();
        } else {
          setUploadLoader(false);
          handlePayrollTimeSheet();
          setTimeSheetModal(true);
          setApiMessage(res.error);
        }
      } catch (err) {
        setUploadLoader(false);
        handlePayrollTimeSheet();
        setTimeSheetModal(true);
        setApiMessage(TIMESHEET_UPLOAD_ERROR);
      }
    };
    reader.onerror = error => {
      errorToast(TIMESHEET_UPLOAD_ERROR);
      console.error(error);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleWeeklyTimeSheetSubmit = async (data, selectedFile) => {
    const reader = new FileReader();
    setUploadLoader(true);
    reader.onload = async () => {
      const base64String = reader.result.split(",")[1];
      const mappedData = (data?.validData || []).map(row => {
        const rawPos = row["Labor Classification"] ?? row["Position"] ?? row.position ?? "";
        const position = typeof rawPos === "string" ? rawPos.trim() : String(rawPos || "");
        const mapped = { ...row, position };
        return mapped;
      });

      const payload = {
        projectId: id,
        fileModel: {
          base64String: base64String,
          fileName: selectedFile.name
        },
        // data: mappedData
        excelData: formatKeys(mappedData)
      };

      try {
        const res = await postApi(`${GET_PROJECT_SUBMITTIMESHEETEXCEL}`, payload);
        if (!res?.error) {
          setUploadLoader(false);
          setTimeSheetModal(true);
          setApiMessage("Timesheet uploaded successfully.");
          handlePayrollTimeSheet();
        } else {
          setUploadLoader(false);
          handlePayrollTimeSheet();
          setTimeSheetModal(true);
          setApiMessage(res.error ?? "Timesheet upload failed.");
        }
      } catch (err) {
        setUploadLoader(false);
        handlePayrollTimeSheet();
        setTimeSheetModal(true);
        setApiMessage(TIMESHEET_UPLOAD_ERROR);
      }
    };
    reader.onerror = error => {
      errorToast(TIMESHEET_UPLOAD_ERROR);
      console.error(error);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handlePayrollSummarySubmit = async (data, selectedFile) => {
    const reader = new FileReader();
    setUploadLoader(true);
    reader.onload = async () => {
      const base64String = reader.result.split(",")[1];
      const excelDataWithCheckNumber = (data?.validData || []).map(item => ({
        ...item,
        checkNumber: item["Paycheck Number"]
      }));

      const payload = {
        fileModel: {
          base64String,
          fileName: selectedFile.name
        },
        // data: mappedData
        excelData: formatKeys(excelDataWithCheckNumber)
      };

      try {
        const res = await postApi(`${GET_PROJECTPAYROLL_SUBMITTIMESHEETEXCEL}?projectId=${id}`, payload);
        if (!res?.error) {
          setUploadLoader(false);
          setPayrollSummaryModal(true);
          setApiMessage("Payroll uploaded successfully.");
          handlePayrollTimeSheet();
        } else {
          setUploadLoader(false);
          setPayrollSummaryModal(true);
          setApiMessage(res.error ?? "Payroll upload failed.");
          handlePayrollTimeSheet();
        }
      } catch (err) {
        setUploadLoader(false);
        handlePayrollTimeSheet();
        setPayrollSummaryModal(true);
        setApiMessage("Payroll Excel Data Failed to Upload");
      }
    };

    reader.onerror = error => {
      errorToast("Payroll Excel Data Failed to Upload");
      console.error(error);
    };

    reader.readAsDataURL(selectedFile);
  };

  const determineSubmitHandler = () => {
    if (selectedValue === "1") {
      return timeSheetValue === "3" ? handleWeeklyTimeSheetSubmit : handleTimeSheetSubmit;
    } else {
      return handlePayrollSummarySubmit;
    }
  };

  const timeSheetMultipleRows = () => {
    const isVersionTwo = projectInfo.cprVersionLabel?.includes("1/31/2028");
    if (selectedValue === "1") {
      if (timeSheetValue === "3") {
        return getTimeSheetFields(isVersionTwo, hasWorkAreas);
      }
      return timeSheetSingleRowFields;
    }
    return getPayrollFields(isVersionTwo, hasWorkAreas);
  };

  useEffect(() => {
    if (!isOpen) return;
    const observer = new MutationObserver(() => {
      const rsiRoot = document.querySelector(".rsi") || document.body;
      const labels = Array.from(rsiRoot.querySelectorAll("label"));
      labels.forEach(label => {
        const txt = (label.textContent || "").trim().toLowerCase();
        if (/instruction/.test(txt)) {
          const option = label.closest(".MuiFormControlLabel-root") || label.closest("label");
          if (option && option.parentElement) {
            option.parentElement.removeChild(option);
          }
        }
      });
      const container = rsiRoot;
      if (!selectedValue) {
        const firstRadio = container.querySelector('input[type="radio"]:not([disabled])');
        if (firstRadio && !firstRadio.checked) {
          firstRadio.click();
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isOpen]);

  return (
    <>
      {projectInfo && (
        <ReactSpreadsheetImport
          isOpen={isOpen}
          uploadStepHook={handleUploadStepHook}
          onClose={handleCloseDialogpopup}
          onSubmit={determineSubmitHandler()}
          fields={timeSheetMultipleRows()}
          customTheme={{
            rsi: {
              overflow: "auto !important",
              Tooltip: {
                backgroundColor: "#1e293b",
                color: "white",
                fontSize: "12px",
                borderRadius: "4px",
                padding: "6px 8px"
              }
            }
          }}
        />
      )}
      {uploadLoader && <Loading />}

      {/* Version Mismatch Warning Dialog */}
      <Dialog
        open={versionWarning.show}
        onClose={versionWarning.onCancel || (() => {})}
        aria-labelledby="version-warning-dialog-title"
        aria-describedby="version-warning-dialog-description"
      >
        <DialogTitle id="version-warning-dialog-title">CPR Version Mismatch Warning</DialogTitle>
        <DialogContent>
          <DialogContentText id="version-warning-dialog-description">
            The uploaded file is for <strong>{versionWarning.fileVersion}</strong>. Your project is using CPR{" "}
            <strong>{versionWarning.projectVersion}</strong>.
            <br />
            <br />
            Do you want to proceed with the upload?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={versionWarning.onCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={versionWarning.onProceed} color="primary" variant="contained" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <DialogModal show={timeSheetModal} title="Message" message={` ${apiMessage}`} onConfirm={handleCancelTimeSheet} showCancel={false} />
      <DialogModal show={payrollSummaryModal} title="Message" message={` ${apiMessage}`} onConfirm={handleCancelPayrollSummary} showCancel={false} />
      <DialogModal show={certifiedPayrollModal} title="Message" message={` ${apiMessage}`} onConfirm={handleCertifiedPayroll} showCancel={false} />
    </>
  );
};

ProjectCertifiedPayrollSpreadsheet.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  timeSheetValue: PropTypes.string.isRequired,
  selectedValue: PropTypes.string.isRequired,
  certifiedPayrollModal: PropTypes.bool.isRequired,
  setCertifiedPayrollModal: PropTypes.func.isRequired,
  apiMessage: PropTypes.string.isRequired,
  setApiMessage: PropTypes.func.isRequired,
  handlePayrollTimeSheet: PropTypes.func.isRequired,
  inputSourceId: PropTypes.number.isRequired,
  projectInfo: PropTypes.object.isRequired
};

export default ProjectCertifiedPayrollSpreadsheet;
